import { NgClass, NgIf, NgStyle } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Optional,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { FormControl, FormGroupDirective } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { UntilDestroy } from '@ngneat/until-destroy';
import { NgxMaskService } from 'ngx-mask';
import { distinctUntilChanged } from 'rxjs';
import { tap } from 'rxjs/operators';

import { PhoneValidatorService } from '@main-application/shared/services/phone-validator.service';
import { CountryCodes, ValidPhoneNumberLength_10 } from '@shared/constants/country-phone-codes.const';
import { InputErrorModule } from '@ui-components/components/input-error/input-error.module';
import { CustomControlAbstract } from '@ui-components/controls/custom-control.abstract';
import { InputModule } from '@ui-components/controls/input/input.module';
import { RadioButtonDropdownModule } from '@ui-components/controls/radio-button-dropdown/radio-button-dropdown.module';

@UntilDestroy()
@Component({
  selector: 'app-phone-number-input',
  templateUrl: './phone-number-input.component.html',
  styleUrls: ['./phone-number-input.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [InputModule, RadioButtonDropdownModule, MatIconModule, NgIf, NgStyle, NgClass, InputErrorModule],
})
export class PhoneNumberInputComponent extends CustomControlAbstract<string> implements OnInit, OnChanges {
  protected cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private maskApplierService: NgxMaskService = inject(NgxMaskService);
  private phoneValidatorService: PhoneValidatorService = inject(PhoneValidatorService);

  @Input() attrLabel: string;
  @Input() declare defaultCode: number;
  @Input() shouldHideDropdownArrow = false;
  @Input() displaySkeleton = false;
  @Input() phoneNumberAsIdentity = false;
  @Input() declare updatePhoneInputValidation: Date;
  @Input() editMode = false;
  @Input() numberRequired = false;

  @Output() phoneNumberInvalid = new EventEmitter<boolean>();
  @Output() blurEvent = new EventEmitter<FocusEvent>();
  @Output() inputReady = new EventEmitter<void>();
  @Output() enterKeyEvent = new EventEmitter<Event>();
  protected readonly countryCodes = CountryCodes;

  constructor(@Optional() formDirective: FormGroupDirective) {
    super(formDirective);
  }

  phoneCodeControl = new FormControl<number>(1);
  phoneNumberControl = new FormControl<string>(null);

  maskPattern = '(000) 000-0000';
  phoneCode: number;
  validPhoneNumberLength = ValidPhoneNumberLength_10;
  notValidFormat = false;
  _inputReady = false;

  ngOnInit(): void {
    this.initControlBase();

    this.control.registerOnDisabledChange(isDisabled => {
      this.setDisabled(isDisabled);
    });

    this.setDisabled(this.control.disabled);

    this.onPhoneNumberControlChange();
    this.onPhoneCodeControlChange();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['updatePhoneInputValidation']?.currentValue) this.isNumberValid();
    if (!changes['displaySkeleton']?.firstChange) this.phoneInputReady();
  }

  isNumberValid(): void {
    this.notValidFormat = this.phoneNumberControl.value && this.phoneNumberControl.invalid;
    this.phoneNumberInvalid.emit(this.notValidFormat);
  }

  phoneInputReady(): void {
    setTimeout(() => {
      if (!this._inputReady) {
        this._inputReady = true;
        this.inputReady.emit();
      }
    });
  }

  writeValue(value: any): void {
    if (value != this.control.value) this.control.setValue(value);

    this.onGetPhoneNumber(this.control.value);
  }

  onBlur($event: FocusEvent): void {
    this.blurEvent.emit($event);
    this.onTouched();
  }

  private setDisabled(isDisabled: boolean): void {
    if (isDisabled) {
      this.phoneNumberControl.disable();
      this.phoneCodeControl.disable();
    } else {
      this.phoneNumberControl.enable();
      this.phoneCodeControl.enable();
    }
  }

  private validateAndEmitValue() {
    const { maskPattern, validPhoneNumberLength } = this.phoneValidatorService.onGetMaskPattern(this.phoneCode);
    this.maskPattern = maskPattern;
    this.validPhoneNumberLength = validPhoneNumberLength;

    const phoneNumberRaw = this.phoneNumberControl.value;
    if (phoneNumberRaw && this.phoneCode) {
      const phoneNumber = phoneNumberRaw.replace(/\D/g, '');
      this.onChanged(`+${this.phoneCode}${phoneNumber}`);
    } else {
      this.onChanged(``);
    }

    this.notValidFormat = phoneNumberRaw && this.phoneNumberControl.invalid;

    this.phoneNumberInvalid.emit(this.notValidFormat);
  }

  private onGetPhoneNumber(value: string): void {
    const { phoneCode, phoneNumber } = this.phoneValidatorService.parsePhoneNumber(value, true);
    this.phoneCode = phoneCode;

    if (phoneCode) {
      const { maskPattern, validPhoneNumberLength } = this.phoneValidatorService.onGetMaskPattern(phoneCode);
      this.maskPattern = maskPattern;
      this.validPhoneNumberLength = validPhoneNumberLength;

      this.phoneCodeControl.setValue(phoneCode, { emitEvent: false });

      if (phoneNumber) {
        const maskedPhone = this.maskApplierService.applyMask(phoneNumber, maskPattern);
        this.phoneNumberControl.setValue(maskedPhone, { emitEvent: false });
      } else {
        this.phoneNumberControl.setValue(null, { emitEvent: false });
      }
    } else {
      const cleanedValue = value?.trim();
      this.phoneNumberControl.setValue(cleanedValue || null, { emitEvent: false });
    }

    this.cdr.detectChanges();
  }

  private onPhoneNumberControlChange(): void {
    this.phoneNumberControl.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe(value => {
        this.validateAndEmitValue();
      })
      .untilDestroyed(this);
  }

  private onPhoneCodeControlChange(): void {
    this.phoneCodeControl.valueChanges
      .pipe(
        distinctUntilChanged(),
        tap(phoneCode => {
          const currentPhoneNumber = this.phoneNumberControl.value;
          const phoneDigits = currentPhoneNumber ? currentPhoneNumber.replace(/\D/g, '') : '';

          this.phoneCode = phoneCode;

          const { maskPattern, validPhoneNumberLength } = this.phoneValidatorService.onGetMaskPattern(phoneCode);
          this.maskPattern = maskPattern;
          this.validPhoneNumberLength = validPhoneNumberLength;

          if (phoneDigits) {
            const maskedPhone = this.maskApplierService.applyMask(phoneDigits, maskPattern);
            this.phoneNumberControl.setValue(maskedPhone, { emitEvent: false });
          } else {
            this.phoneNumberControl.setValue(null, { emitEvent: false });
          }

          this.validateAndEmitValue();
        })
      )
      .subscribe()
      .untilDestroyed(this);
  }
}
